import { trackEvent } from '@patrianna/analytics'
import { openDialog } from '@patrianna/dialog-manager'
import type { DailyBonusInfo } from '@patrianna/shared-patrianna-types/store/AccountBonusesModule'
import { useMemo } from 'react'

import { IS_BRAND_PULSZ } from 'src/config/brands'
import useBuilderCMSData from 'src/hooks/useBuilderCMSData'
import { useAppSelector } from 'src/store/hooks'
import { sweepstakeEnabledSelector } from 'src/store/modules/appConfig/selectors'
import {
  getDailyBonusesSelector,
  isBonusesLoadingSelector,
  isDailyBonusAvailableSelector,
} from 'src/store/modules/bonusesFlow/selectors'

import useCountdownTimer from '../../VerifyEmail/utils/useCountdownTimer'

import { WidgetsVariant, WidgetColor } from './types'
import type { TimerWidget } from './types'

const DAILY_GIFT_WIDGET_DATA_MODEL = process.env.CMS_DATA_MODEL_DAILY_GIFT_WIDGET

export const useDailyGiftWidget = (): TimerWidget => {
  const isDailyBonusAvailable = useAppSelector(isDailyBonusAvailableSelector)
  const dailyBonuses = useAppSelector(getDailyBonusesSelector)
  const isBonusesLoading = useAppSelector(isBonusesLoadingSelector)
  const isScEnabled = useAppSelector(sweepstakeEnabledSelector)

  const { builderData: dailyGiftData, builderDataLoaded } = useBuilderCMSData(DAILY_GIFT_WIDGET_DATA_MODEL)
  const builderData = dailyGiftData[0]?.data

  const nextAvailableBonusTime = useMemo(() => {
    const nextBonus = dailyBonuses?.find((bonus: DailyBonusInfo) => bonus.status === 'initial')

    return nextBonus?.time ? new Date(nextBonus.time).valueOf() : 0
  }, [dailyBonuses])

  const { timer, isTimerLoading } = useCountdownTimer(nextAvailableBonusTime, 1000, true)
  const imgSrc = isDailyBonusAvailable ? builderData?.claimDailyGiftUrl : builderData?.dailyGiftTimerUrl
  const hasBuilderData = !!builderData && !!imgSrc

  const timerValue = isBonusesLoading || isTimerLoading ? '...' : timer || '--:--:--'

  const handleDailyGiftClick = () => {
    trackEvent('daily_login_widget_clicked')

    if (dailyBonuses) {
      openDialog({
        dialogName: 'DAILY_REWARD_DIALOG',
        dialogProps: { isClaimAvailable: isDailyBonusAvailable, nextAvailableBonusTime },
      })
    }
  }

  return {
    id: WidgetsVariant.DAILY_GIFT,
    isWidgetVisible: IS_BRAND_PULSZ && isScEnabled && hasBuilderData && builderDataLoaded,
    imgSrc,
    imgAlt: builderData?.imageAlt || 'Daily gift',
    timer: !isDailyBonusAvailable && timerValue,
    color: WidgetColor.GREEN,
    onClick: handleDailyGiftClick,
  }
}
